import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowRight,
  Bot,
  ChevronDown,
  MessageCircle,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useKernel } from "./Shortener/hooks/AIKernel";

const routeLabels = {
  "/": "Home",
  "/about": "About",
  "/features": "Features",
  "/contact": "Contact",
  "/dashboard": "Video studio",
};

const assistantReply = (question, pathname, dominantInterest) => {
  const text = question.toLowerCase();

  if (/edit|trim|caption|subtitle|audio|video|studio|export|upload/.test(text)) {
    return {
      text: "The Video Studio is the best place to start. Open the studio to upload a clip, then use its editing tools for trimming, transcription, audio, and branding. Sign-in or plan access may be required.",
      action: { label: "Open Video Studio", path: "/dashboard" },
    };
  }

  if (/feature|tool|capabilit|what can|product/.test(text)) {
    return {
      text: "Explore the capabilities overview for a guided look at the product. If you want to try the editing workflow, the Video Studio is one step away.",
      action: { label: "Explore capabilities", path: "/features" },
    };
  }

  if (/contact|support|help|human|email|sales|talk/.test(text)) {
    return {
      text: "For a person-to-person conversation, the contact page has the available contact details and a message form.",
      action: { label: "Contact the team", path: "/contact" },
    };
  }

  if (/about|how|workflow|process|company/.test(text)) {
    return {
      text: "The overview page explains the workflow, use cases, and product approach. I can also take you directly to the studio to try the tools.",
      action: { label: "View the overview", path: "/about" },
    };
  }

  if (/recommend|suggest|next|start|best|improv/.test(text)) {
    if (/video|editor|trim|audio|transcri|brand/i.test(dominantInterest)) {
      return {
        text: `Your recent activity points toward ${dominantInterest.replace(/[_-]+/g, " ")}. A useful next step is to try that workflow in the Video Studio; I can also show you the broader capability set.`,
        action: { label: "Continue in the studio", path: "/dashboard" },
      };
    }
    return {
      text: `You’re viewing ${routeLabels[pathname] || "VideoKing"}. For a quick product tour, explore the capabilities; to try the editing workflow, open the Video Studio. Suggestions are based on your local AIKernel activity.`,
      action: { label: "Explore capabilities", path: "/features" },
    };
  }

  return {
    text: `I can guide you around ${routeLabels[pathname] || "VideoKing"} and its video tools. Ask about editing, product capabilities, or getting in touch. My suggestions use the local AIKernel and do not send your message to an AI service.`,
    action: { label: "Explore capabilities", path: "/features" },
  };
};

const AIKernelAssistant = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { agentState, reportBehavior } = useKernel();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      text: "Welcome to VideoKing. I can help you find the right tools and next steps across the studio.",
    },
  ]);
  const scrollRef = useRef(null);
  const dominantInterest = agentState?.dominantInterest || "general";
  const suggestions = useMemo(
    () =>
      location.pathname === "/dashboard"
        ? ["How do I edit a video?", "What can the studio do?"]
        : ["What can I do here?", "Recommend a next step"],
    [location.pathname]
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = (value) => {
    const text = value.trim();
    if (!text) return;

    reportBehavior("assistant_message_sent", 1, "behavior");
    const lowerText = text.toLowerCase();
    if (/edit|trim|caption|subtitle|audio|video|studio|export|upload/.test(lowerText)) {
      reportBehavior("assistant_video_workflow_interest", 2, "interest");
    } else if (/feature|tool|capabilit|product/.test(lowerText)) {
      reportBehavior("assistant_product_interest", 2, "interest");
    } else if (/contact|support|help|email|sales/.test(lowerText)) {
      reportBehavior("assistant_support_interest", 2, "interest");
    }

    const reply = assistantReply(text, location.pathname, dominantInterest);
    setMessages((current) => [
      ...current,
      { id: `${Date.now()}-user`, role: "user", text },
      { id: `${Date.now()}-assistant`, role: "assistant", ...reply },
    ]);
    setDraft("");
  };

  const handleAction = (path) => {
    reportBehavior(`assistant_navigation_${path.replace(/\W+/g, "_")}`, 2, "interest");
    navigate(path);
  };

  return (
    <Box className="kernel-assistant">
      {open && (
        <Paper
          id="kernel-assistant-panel"
          className="kernel-assistant__panel"
          elevation={0}
          role="dialog"
          aria-labelledby="kernel-assistant-title"
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
        >
          <Box className="kernel-assistant__header">
            <Box className="kernel-assistant__identity">
              <Box className="kernel-assistant__avatar" aria-hidden="true">
                <Sparkles size={17} />
              </Box>
              <Box>
                <Typography id="kernel-assistant-title" className="kernel-assistant__title">
                  Studio guide
                </Typography>
                <Typography className="kernel-assistant__status">
                  <span className="kernel-assistant__status-dot" />
                  Local AIKernel · {routeLabels[location.pathname] || "VideoKing"}
                </Typography>
              </Box>
            </Box>
            <IconButton
              aria-label="Close assistant"
              onClick={() => setOpen(false)}
              size="small"
              sx={{ color: "inherit" }}
            >
              <ChevronDown size={19} />
            </IconButton>
          </Box>

          <Box className="kernel-assistant__messages" ref={scrollRef} aria-live="polite">
            <Typography className="kernel-assistant__privacy">
              Suggestions are based on local activity signals. Chat messages stay in this browser.
            </Typography>
            {messages.map((message) => (
              <Box
                key={message.id}
                className={`kernel-assistant__message kernel-assistant__message--${message.role}`}
              >
                {message.role === "assistant" && (
                  <Bot className="kernel-assistant__message-icon" size={16} aria-hidden="true" />
                )}
                <Box className="kernel-assistant__bubble">
                  <Typography className="kernel-assistant__message-text">{message.text}</Typography>
                  {message.action && (
                    <Button
                      size="small"
                      endIcon={<ArrowRight size={15} />}
                      onClick={() => handleAction(message.action.path)}
                      className="kernel-assistant__action"
                    >
                      {message.action.label}
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          <Box className="kernel-assistant__suggestions">
            {messages.length === 1 &&
              suggestions.map((suggestion) => (
                <Button
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  onClick={() => sendMessage(suggestion)}
                  className="kernel-assistant__suggestion"
                >
                  {suggestion}
                </Button>
              ))}
          </Box>

          <Divider />
          <Box
            component="form"
            className="kernel-assistant__composer"
            onSubmit={(event) => {
              event.preventDefault();
              sendMessage(draft);
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask about the studio..."
              inputProps={{ "aria-label": "Ask the VideoKing assistant" }}
              className="kernel-assistant__input"
            />
            <IconButton
              type="submit"
              aria-label="Send message"
              disabled={!draft.trim()}
              className="kernel-assistant__send"
            >
              <Send size={17} />
            </IconButton>
          </Box>
        </Paper>
      )}

      <Fab
        color="primary"
        aria-label={open ? "Close VideoKing assistant" : "Open VideoKing assistant"}
        aria-expanded={open}
        aria-controls={open ? "kernel-assistant-panel" : undefined}
        onClick={() => {
          if (!open) reportBehavior("assistant_opened", 0.5, "behavior");
          setOpen(!open);
        }}
        className={`kernel-assistant__launcher${agentState?.ui?.highlightCTAs ? " kernel-assistant__launcher--highlighted" : ""}`}
      >
        {open ? <X size={21} /> : <MessageCircle size={21} />}
      </Fab>
    </Box>
  );
};

export default AIKernelAssistant;
